import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getInstallationSettings, putInstallationSettings } from '../../actions';
import { FetchError, IInstallationSettings, ISettingsResponse } from '../../models';
import { convertShopifySettings, IShopifySettingsResponse } from '../../models/shopify';
import { integrationTypes } from '../../static/constants/integrationTypes';
import { RootState } from '../rootReducer';

export interface ISettingsState {
    settings: IInstallationSettings;
    isSyncing: boolean;
    isFetching: boolean;
    error?: Error;
}

export const initialState: ISettingsState = {
    settings: {
        installationId: '',
    },
    isSyncing: false,
    isFetching: false,
};

const name = 'settings';

const settingsSlice = createSlice({
    initialState,
    name,
    reducers: {
        updateSettingsBegin(state) {
            state.isSyncing = true;
        },
        updateSettingsSuccess(state, action: PayloadAction<Partial<IInstallationSettings>>) {
            state.settings = {
                ...state.settings,
                ...action.payload,
            };
            state.isSyncing = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getSettingsAction.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getSettingsAction.fulfilled, (state, { payload }) => {
                switch (payload.integrationType) {
                    case integrationTypes.SHOPIFY:
                        const shopifySettings = payload as IShopifySettingsResponse;
                        shopifySettings?.accountAndTaxMappings?.forEach((accountAndTaxMapping) => {
                            accountAndTaxMapping.xeroTaxTypeOnLoad = accountAndTaxMapping.xeroTaxType;
                            accountAndTaxMapping.xeroAccountIdOnLoad = accountAndTaxMapping.xeroAccountId;
                        });

                        state.settings = {
                            ...shopifySettings,
                            ...convertShopifySettings(shopifySettings),
                        };
                        break;
                    default:
                        state.settings = payload;
                }
                state.isFetching = false;
                state.error = undefined;
            })
            .addCase(getSettingsAction.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.error = payload;
            })
            .addCase(putSettingsAction.pending, (state) => {
                state.isSyncing = true;
            })
            .addCase(putSettingsAction.fulfilled, (state) => {
                state.isSyncing = false;
                state.error = undefined;
            })
            .addCase(putSettingsAction.rejected, (state, { payload }) => {
                state.isSyncing = false;
                state.error = payload;
            });
    },
});

export const putSettingsAction = createAsyncThunk<
    IInstallationSettings | object, // The  parameter type
    IInstallationSettings | object, // the return type of the thunk to be used in the `extraReducers`
    {
        // Type the ReduxState and the reject value
        state: RootState;
        rejectValue: FetchError;
    }
>(
    `${name}/putSettingsAction`, // name the thunkaction
    async (settings, { getState, rejectWithValue }) => {
        const state = getState();
        try {
            await putInstallationSettings(state.user.user, state.installation.installation, settings);

            return settings;
        } catch (error) {
            if (error instanceof FetchError) {
                // api response errors i.e. 400 and 404
                return rejectWithValue({
                    name: error.name,
                    message: error.message,
                    code: error.code,
                    body: error.body,
                });
            }
            return rejectWithValue(error);
        }
    }
);

export const getSettingsAction = createAsyncThunk<
    ISettingsResponse,
    void,
    { state: RootState; rejectValue: FetchError }
>(`${name}/getSettings`, async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState();

        return {
            ...(await getInstallationSettings(state.user.user, state.installation.installation)),
            integrationType: state.installation.installation.integrationType,
        };
    } catch (error) {
        if (error instanceof FetchError) {
            // api response errors i.e. 400 and 404
            return rejectWithValue({ name: error.name, message: error.message, code: error.code, body: error.body });
        }
        return rejectWithValue(error);
    }
});

export const { updateSettingsBegin, updateSettingsSuccess } = settingsSlice.actions;

export const settingsSelectors = {
    settings: (state: RootState) => state.settings.settings,
    isFetching: (state: RootState) => state.settings.isFetching,
    isSyncing: (state: RootState) => state.settings.isSyncing,
    error: (state: RootState) => state.settings.error,
};

export default settingsSlice.reducer;
